import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSync,
  FaSearch,
  FaTimes,
  FaCheckCircle,
  FaClock,
  FaMoneyBillWave,
} from "react-icons/fa";

function FacturaList() {
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔎 filtros
  const [busqueda, setBusqueda] = useState("");
  const [filtroMetodoPago, setFiltroMetodoPago] = useState("TODOS");

  const navigate = useNavigate();

  useEffect(() => {
    cargarFacturas();
  }, []);

  const cargarFacturas = async () => {
    setLoading(true);
    try {
      const response = await api.get("/facturas");
      setFacturas(response.data || []);
    } catch (error) {
      console.error(error);
      toast.error("❌ Error al cargar facturas");
    } finally {
      setLoading(false);
    }
  };

  // 👉 ahora sólo navega al detalle
  const abrirDetalle = (facturaId) => {
    navigate(`/facturas/${facturaId}`);
  };

  // 🧮 facturas filtradas (ID, mesa, mesero + método de pago)
  const facturasFiltradas = useMemo(() => {
    let data = [...facturas];

    if (filtroMetodoPago !== "TODOS") {
      data = data.filter((f) => f.metodoPago === filtroMetodoPago);
    }

    if (busqueda.trim() !== "") {
      const q = busqueda.toLowerCase();
      data = data.filter((f) => {
        const idFactura = f.id?.toString() || "";
        const mesa = f.pedido?.mesa?.numero?.toString() || "";
        const mesero = f.pedido?.mesero?.nombre?.toLowerCase() || "";
        return idFactura.includes(q) || mesa.includes(q) || mesero.includes(q);
      });
    }

    return data;
  }, [facturas, filtroMetodoPago, busqueda]);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="mt-4 bg-white/95 text-slate-900 dark:bg-slate-900/90 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-amber-500 dark:text-amber-300 flex items-center gap-2">
              <FaMoneyBillWave />
              Listado de Facturas
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Consulta las facturas generadas y revisa su detalle.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            {/* Filtro método de pago */}
            <select
              value={filtroMetodoPago}
              onChange={(e) => setFiltroMetodoPago(e.target.value)}
              className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400"
            >
              <option value="TODOS">Todos los métodos</option>
              <option value="EFECTIVO">Efectivo</option>
              <option value="TARJETA">Tarjeta</option>
              <option value="TRANSFERENCIA">Transferencia</option>
            </select>

            {/* Buscador: ID, mesa o mesero */}
            <div className="flex items-center rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 overflow-hidden">
              <span className="pl-2 pr-1 text-slate-400">
                <FaSearch className="w-3.5 h-3.5" />
              </span>
              <input
                type="text"
                placeholder="Buscar por ID, mesa o mesero..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="px-2 py-1.5 text-xs sm:text-sm bg-transparent text-slate-800 dark:text-slate-100 focus:outline-none"
                style={{ width: "190px" }}
              />
              {busqueda && (
                <button
                  type="button"
                  onClick={() => setBusqueda("")}
                  className="px-2 py-1.5 text-xs text-slate-400 hover:text-red-400"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <button
              onClick={cargarFacturas}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-400 dark:border-slate-600 text-xs sm:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <FaSync className="text-sky-500" />
              Actualizar
            </button>
          </div>
        </div>

        {/* Info / contador */}
        <div className="flex justify-between items-center mb-3">
          <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            {loading
              ? "Cargando facturas..."
              : `Mostrando ${facturasFiltradas.length} factura${
                  facturasFiltradas.length !== 1 ? "s" : ""
                }`}
          </span>
        </div>

        {/* Tabla */}
        <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-800">
          {facturasFiltradas.length === 0 && !loading ? (
            <div className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              No hay facturas registradas.
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[900px] md:min-w-full text-sm">
                <thead className="bg-slate-100 dark:bg-slate-950/80">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                      ID
                    </th>
                    <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                      Fecha Emisión
                    </th>
                    <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                      Método Pago
                    </th>
                    <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                      Total
                    </th>
                    <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                      Estado
                    </th>
                    <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                      Pedido
                    </th>
                    <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                      Mesa
                    </th>
                    <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                      Mesero
                    </th>
                    <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {facturasFiltradas.map((factura) => (
                    <tr
                      key={factura.id}
                      className="odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/60 dark:even:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <td className="px-4 py-2 text-left align-middle font-medium">
                        {factura.id}
                      </td>
                      <td className="px-4 py-2 text-center align-middle">
                        {factura.fechaEmision
                          ? new Date(factura.fechaEmision).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-4 py-2 text-center align-middle">
                        {factura.metodoPago || "—"}
                      </td>
                      <td className="px-4 py-2 text-center align-middle font-semibold">
                        ${factura.total?.toLocaleString() || "0"}
                      </td>
                      <td className="px-4 py-2 text-center align-middle">
                        <span
                          className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-semibold border gap-1 ${
                            factura.pago
                              ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/60"
                              : "bg-amber-500/15 text-amber-300 border-amber-500/60"
                          }`}
                        >
                          {factura.pago ? (
                            <>
                              <FaCheckCircle className="text-emerald-300" />
                              Pagado
                            </>
                          ) : (
                            <>
                              <FaClock className="text-amber-300" />
                              Pendiente
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center align-middle">
                        {factura.pedido?.id || "—"}
                      </td>
                      <td className="px-4 py-2 text-center align-middle">
                        {factura.pedido?.mesa?.numero || "—"}
                      </td>
                      <td className="px-4 py-2 text-center align-middle">
                        {factura.pedido?.mesero?.nombre || "—"}
                      </td>
                      <td className="px-4 py-2 text-center align-middle">
                        <button
                          onClick={() => abrirDetalle(factura.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-sky-500 text-sky-300 hover:bg-sky-500 hover:text-slate-900 transition-colors"
                        >
                          <FaSearch />
                          Ver detalle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default FacturaList;
